import { IOption } from '@shared/models/icommons.model';
import { QueryCriteria } from './search-filters.model';

/**
 * Discriminates how a filter carries compound filters in the search state and wire format.
 *
 * - Standalone    No associations; compound filters live on the root QueryCriteria.
 * - AssociatedText Has associations (group filter); compound filters live on the text
 *                  sub-filter inside the group's values[] array.
 * - Naics          inputType === 'naics'; same wire position as AssociatedText
 *                  (sub-filter inside values[]) but isolated for future divergence.
 */
export enum CompoundFilterType {
  Standalone = 'Standalone',
  AssociatedText = 'AssociatedText',
  Naics = 'Naics',
}

/**
 * Strategy interface for reading/writing a compound filter selection on the stored QueryCriteria.
 *
 * Each builder knows where `compoundFilters` live in the filter tree for its type:
 * - Standalone   → on the root filter object
 * - AssociatedText / Naics → on the sub-filter identified by `subFilterKey` inside values[]
 *
 * Centralizing BOTH the read and the write side per type here (rather than re-deriving
 * "is this Group or Standalone" independently in every consumer) is deliberate: it's what
 * previously caused fixes for one filter type to silently miss/break another. Any new code
 * that needs to know "where do compound filters live for this filter" should go through
 * `getCompoundFilterBuilder`, not re-implement its own placement check.
 *
 * Note: this interface is intentionally limited to selection placement/retrieval.
 * Value injection (copying parent values onto each selected compound filter before
 * sending to the API) is handled separately by `injectCompoundFilterValues`.
 */
export interface CompoundFilterBuilder {
  /**
   * Returns a new filter with the selected compound filters applied to the correct position.
   * Compound filters are stored WITHOUT values at this stage.
   *
   * @param parentFilter  The QueryCriteria entry from searchParams.filters
   * @param subFilterKey  Key of the specific sub-filter that owns the compound filters
   *                      (equals parentFilter.key for Standalone)
   * @param selectedKeys  Keys chosen by the user in CompoundFilterSelectionComponent
   * @param catalogFilters The full IOption[] catalog for this filter (from IOption.compoundFilters)
   */
  applySelection(
    parentFilter: QueryCriteria,
    subFilterKey: number,
    selectedKeys: number[],
    catalogFilters: IOption[]
  ): QueryCriteria;

  /**
   * Reads back the currently selected compound-filter keys from a stored QueryCriteria tree.
   *
   * Returns `undefined` when no selection has been stored yet at all (distinct from an
   * explicit empty array), so callers can tell "never seeded" apart from "user selected
   * nothing" and fall back to defaults only in the former case.
   *
   * @param parentFilter  The QueryCriteria entry to read from (root or previously stored state)
   * @param subFilterKey  Key of the specific sub-filter that owns the compound filters
   *                      (equals parentFilter.key for Standalone)
   */
  readSelectedKeys(parentFilter: QueryCriteria, subFilterKey: number): number[] | undefined;
}
